import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const RequestWorkflowTable = ({ requests, onViewDetails, onStatusUpdate }) => {
  const [sortField, setSortField] = useState('requestedDate');
  const [sortDirection, setSortDirection] = useState('desc');

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedRequests = [...requests]?.sort((a, b) => {
    let aVal = a?.[sortField];
    let bVal = b?.[sortField];

    if (typeof aVal === 'string') {
      aVal = aVal?.toLowerCase();
      bVal = bVal?.toLowerCase();
    }

    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const getStatusBadge = (status) => {
    const statusStyles = {
      draft: { bg: 'bg-gray-500/10', text: 'text-gray-600', label: 'Draft' },
      submitted: { bg: 'bg-blue-500/10', text: 'text-blue-600', label: 'Submitted' },
      under_review: { bg: 'bg-purple-500/10', text: 'text-purple-600', label: 'Under Review' },
      approved: { bg: 'bg-green-500/10', text: 'text-green-600', label: 'Approved' },
      rejected: { bg: 'bg-red-500/10', text: 'text-red-600', label: 'Rejected' },
      in_development: { bg: 'bg-amber-500/10', text: 'text-amber-600', label: 'In Development' },
      testing: { bg: 'bg-indigo-500/10', text: 'text-indigo-600', label: 'Testing' },
      deployed: { bg: 'bg-teal-500/10', text: 'text-teal-600', label: 'Deployed' },
      completed: { bg: 'bg-emerald-500/10', text: 'text-emerald-600', label: 'Completed' },
      cancelled: { bg: 'bg-gray-500/10', text: 'text-gray-600', label: 'Cancelled' }
    };
    return statusStyles?.[status] || statusStyles?.draft;
  };

  const getPriorityBadge = (priority) => {
    const priorityStyles = {
      critical: { bg: 'bg-red-500/10', text: 'text-red-600', label: 'Critical' },
      high: { bg: 'bg-orange-500/10', text: 'text-orange-600', label: 'High' },
      medium: { bg: 'bg-yellow-500/10', text: 'text-yellow-600', label: 'Medium' },
      low: { bg: 'bg-green-500/10', text: 'text-green-600', label: 'Low' }
    };
    return priorityStyles?.[priority] || priorityStyles?.medium;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const SortableHeader = ({ field, label }) => (
    <th
      className="text-left py-3 px-4 text-sm font-medium text-muted-foreground cursor-pointer hover:text-foreground transition-colors"
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center gap-2">
        {label}
        {sortField === field && (
          <Icon
            name={sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown'}
            size={14}
          />
        )}
      </div>
    </th>
  );

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-border">
            <SortableHeader field="requestNumber" label="Request #" />
            <SortableHeader field="companyName" label="Client" />
            <SortableHeader field="requestTitle" label="Title" />
            <SortableHeader field="customizationType" label="Type" />
            <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Status</th>
            <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Priority</th>
            <SortableHeader field="estimatedCostKina" label="Est. Cost (K)" />
            <SortableHeader field="developmentProgressPercentage" label="Progress" />
            <SortableHeader field="assignedDeveloper" label="Assigned To" />
            <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Actions</th>
          </tr>
        </thead>
        <tbody>
          {sortedRequests?.length === 0 ? (
            <tr>
              <td colSpan="10" className="text-center py-8 text-muted-foreground">
                No customization requests found
              </td>
            </tr>
          ) : (
            sortedRequests?.map((request) => {
              const statusBadge = getStatusBadge(request?.requestStatus);
              const priorityBadge = getPriorityBadge(request?.priority);

              return (
                <tr key={request?.id} className="border-b border-border hover:bg-muted/50 transition-colors">
                  <td className="py-3 px-4 text-sm font-medium text-foreground">
                    {request?.requestNumber}
                  </td>
                  <td className="py-3 px-4">
                    <div>
                      <p className="text-sm font-medium text-foreground">{request?.companyName}</p>
                      <p className="text-xs text-muted-foreground">{request?.companyEmail}</p>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <div className="max-w-xs">
                      <p className="text-sm font-medium text-foreground truncate">{request?.requestTitle}</p>
                      <p className="text-xs text-muted-foreground truncate">{request?.requestDescription}</p>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-sm text-foreground">
                    {request?.customizationType?.replace(/_/g, ' ')?.replace(/\b\w/g, l => l?.toUpperCase())}
                  </td>
                  <td className="py-3 px-4">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${statusBadge?.bg} ${statusBadge?.text}`}>
                      {statusBadge?.label}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${priorityBadge?.bg} ${priorityBadge?.text}`}>
                      {priorityBadge?.label}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm font-medium text-foreground">
                    K{(request?.estimatedCostKina || 0)?.toLocaleString()}
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-muted rounded-full h-2 overflow-hidden">
                        <div
                          className="bg-primary h-full transition-all"
                          style={{ width: `${request?.developmentProgressPercentage || 0}%` }}
                        />
                      </div>
                      <span className="text-xs font-medium text-foreground">
                        {Math.round(request?.developmentProgressPercentage || 0)}%
                      </span>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-sm text-foreground">
                    {request?.assignedDeveloper || 'Unassigned'}
                  </td>
                  <td className="py-3 px-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onViewDetails?.(request)}
                    >
                      <Icon name="Eye" size={14} className="mr-1" />
                      View
                    </Button>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
};

export default RequestWorkflowTable;